/**
 * reCAPTCHA v3 Service
 * 
 * This service handles reCAPTCHA v3 token generation.
 * Make sure to set VUE_APP_RECAPTCHA_SITE_KEY in your environment variables.
 */

/**
 * Loads reCAPTCHA script and initializes it
 * @param {string} siteKey - The reCAPTCHA site key
 * @returns {Promise} - Promise that resolves when reCAPTCHA is loaded
 */
function loadRecaptcha(siteKey) {
  return new Promise((resolve, reject) => {
    if (window.grecaptcha && window.grecaptcha.ready) {
      window.grecaptcha.ready(() => {
        resolve();
      });
      return;
    }

    // Check if script is already loading
    if (document.querySelector('script[src*="recaptcha/api.js"]')) {
      // Wait for it to load
      const checkInterval = setInterval(() => {
        if (window.grecaptcha && window.grecaptcha.ready) {
          window.grecaptcha.ready(() => {
            clearInterval(checkInterval);
            resolve();
          });
        }
      }, 100);
      return;
    }

    // Load the script
    const script = document.createElement('script');
    script.src = `https://www.google.com/recaptcha/api.js?render=${siteKey}`;
    script.async = true;
    script.defer = true;
    script.onload = () => {
      window.grecaptcha.ready(() => {
        resolve();
      });
    };
    script.onerror = () => {
      reject(new Error('Failed to load reCAPTCHA script'));
    };
    document.head.appendChild(script);
  });
}

/**
 * Gets a reCAPTCHA v3 token
 * @param {string} action - The action name (e.g., 'register', 'register_collapsed', 'register_user')
 * @returns {Promise<string>} - Promise that resolves with the reCAPTCHA token
 */
export async function getRecaptchaToken(action = 'register') {
  const siteKey = process.env.VUE_APP_RECAPTCHA_SITE_KEY;
  
  if (!siteKey) {
    console.warn('VUE_APP_RECAPTCHA_SITE_KEY is not set. reCAPTCHA will not work.');
    return null;
  }

  try {
    await loadRecaptcha(siteKey);
    
    return new Promise((resolve, reject) => {
      window.grecaptcha.ready(() => {
        window.grecaptcha.execute(siteKey, { action })
          .then((token) => {
            resolve(token);
          })
          .catch((error) => {
            console.error('reCAPTCHA execution error:', error);
            reject(error);
          });
      });
    });
  } catch (error) {
    console.error('reCAPTCHA load error:', error);
    return null;
  }
}

export default {
  getRecaptchaToken
};
